<template>
  <Modal
    :visible="visible"
    :placement="placement"
    :width="dialogWidth"
    :aria-labelledby="titleId"
    :aria-describedby="bodyId"
    :close-on-backdrop="closeOnBackdrop && !busy"
    :close-on-escape="closeOnEscape && !busy"
    :show-close-button="!busy"
    :initial-focus-selector="initialFocusSelector"
    @close="requestClose"
  >
    <template #header>
      <UnitButton
        v-if="showBack"
        class="confirm-dialog__back"
        variant="text"
        size="micro"
        icon-only
        :aria-label="backLabel"
        :disabled="busy"
        data-confirm-dialog-back
        @click="requestBack"
      >
        <template #leading>
          <span class="confirm-dialog__back-glyph" aria-hidden="true">←</span>
        </template>
      </UnitButton>
      <h3 :id="titleId" class="confirm-dialog__title">
        {{ title }}
      </h3>
    </template>

    <div
      :id="bodyId"
      class="confirm-dialog__body"
      :class="[
        `confirm-dialog__body--${tone}`,
        { 'confirm-dialog__body--compact': compact },
      ]"
    >
      <slot
        name="body"
        :confirm="requestConfirm"
        :close="requestClose"
        :back="requestBack"
      >
        <p v-if="message" class="confirm-dialog__message">{{ message }}</p>
        <ul v-if="details.length" class="confirm-dialog__details">
          <li v-for="(detail, index) in details" :key="index">
            {{ detail }}
          </li>
        </ul>
      </slot>
    </div>

    <template #footer>
      <div class="confirm-dialog__actions">
        <slot
          name="actions"
          :confirm="requestConfirm"
          :close="requestClose"
          :back="requestBack"
          :busy="busy"
          :confirm-disabled="confirmUnavailable"
        >
          <UnitButton
            v-if="showCancel"
            variant="secondary"
            :disabled="busy"
            data-confirm-dialog-cancel
            @click="requestClose"
          >
            {{ cancelLabel }}
          </UnitButton>
          <UnitButton
            :variant="confirmVariant"
            :disabled="confirmDisabled"
            :loading="busy"
            :loading-label="busyLabel"
            data-confirm-dialog-confirm
            @click="requestConfirm"
          >
            {{ confirmLabel }}
          </UnitButton>
        </slot>
      </div>
    </template>
  </Modal>
</template>

<script setup>
import { computed } from "vue";
import Modal from "@/components/GenericComponents/Modal.vue";
import UnitButton from "@/components/GenericComponents/UnitButton.vue";

let confirmDialogInstanceCount = 0;

const props = defineProps({
  visible: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    required: true,
  },
  message: {
    type: String,
    default: "",
  },
  details: {
    type: Array,
    default: () => [],
  },
  tone: {
    type: String,
    default: "neutral",
    validator: (value) => ["neutral", "danger", "success"].includes(value),
  },
  placement: {
    type: String,
    default: "center",
    validator: (value) => ["center", "sheet"].includes(value),
  },
  compact: {
    type: Boolean,
    default: false,
  },
  showCancel: {
    type: Boolean,
    default: true,
  },
  confirmLabel: {
    type: String,
    default: "Confirm",
  },
  cancelLabel: {
    type: String,
    default: "Cancel",
  },
  busy: {
    type: Boolean,
    default: false,
  },
  confirmDisabled: {
    type: Boolean,
    default: false,
  },
  closeOnBackdrop: {
    type: Boolean,
    default: true,
  },
  closeOnEscape: {
    type: Boolean,
    default: true,
  },
  showBack: {
    type: Boolean,
    default: false,
  },
  backLabel: {
    type: String,
    default: "Back",
  },
});

const emit = defineEmits(["confirm", "close", "back"]);
const instanceId = ++confirmDialogInstanceCount;
const titleId = `unit-confirm-dialog-title-${instanceId}`;
const bodyId = `unit-confirm-dialog-body-${instanceId}`;

const confirmVariant = computed(() =>
  props.tone === "danger" ? "danger" : "primary",
);
const confirmUnavailable = computed(
  () => props.busy || props.confirmDisabled,
);
const dialogWidth = computed(() => (props.compact ? "280px" : "420px"));
const initialFocusSelector = computed(() =>
  props.tone === "danger" && props.showCancel && !props.busy
    ? "[data-confirm-dialog-cancel]"
    : "",
);
const busyLabel = computed(() => `${props.confirmLabel}…`);

const requestConfirm = () => {
  if (!confirmUnavailable.value) emit("confirm");
};

const requestClose = () => {
  if (!props.busy) emit("close");
};

const requestBack = () => {
  if (!props.busy) emit("back");
};
</script>

<style scoped>
.confirm-dialog__title {
  flex: 1;
  margin: 0;
  font-size: var(--font-size-title);
  font-weight: var(--font-weight-semibold);
  text-align: left;
  overflow-wrap: anywhere;
}

.confirm-dialog__back {
  flex: 0 0 auto;
}

.confirm-dialog__back-glyph {
  font-size: var(--font-size-body);
  line-height: 1;
}

.confirm-dialog__body {
  color: var(--color-text-primary);
}

.confirm-dialog__body--danger {
  color: var(--color-feedback-danger-foreground);
}

.confirm-dialog__body--success {
  color: var(--color-feedback-success-foreground-muted);
}

.confirm-dialog__body--compact {
  font-size: var(--font-size-label);
}

.confirm-dialog__message {
  margin: 0;
  text-align: left;
  overflow-wrap: anywhere;
}

.confirm-dialog__details {
  display: grid;
  gap: var(--space-2);
  margin: var(--space-4) 0 0;
  padding-left: var(--space-6);
  color: var(--color-text-secondary);
}

.confirm-dialog__actions {
  display: flex;
  flex: 1;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: var(--space-4);
}
</style>
